import React from 'react';
import { Box, Typography, Tooltip, Paper } from '@material-ui/core';
import { useTimelineViewStyles } from '../styles/timeline-view.styles';
import { TimeMarker } from '../types';
import { getFullDateTimeLabel } from '../utils/timeline-processing';
import { PipelineTimeMarker } from '../types';
import { calculateTimelineMinWidth, calculateTimeMarkerSmartPositioning } from '../utils/positioning';

/**
 * Props interface for the TimeMarkerPaper component
 */
interface TimeMarkerPaperProps {
  /** Pipeline time marker object containing timing and position data */
  marker: PipelineTimeMarker;
  /** Index for unique key generation */
  index: number;
}
/**
 * TimeMarkerPaper Component
 * 
 * Renders a single time marker paper for pipeline start/end times with
 * vertical connection lines and interactive tooltips. Displays formatted
 * date/time information and differentiates between start and end markers
 * through positioning and styling.
 * 
 * @component
 * @param props - The component props
 * @param props.marker - Pipeline time marker with position and timing data
 * @param props.index - Unique index for React key prop
 * 
 * @returns A positioned time marker paper with tooltip and connection line
 */
export const TimeMarkerPaper = React.memo(({ marker, index }: TimeMarkerPaperProps) => {
  const classes = useTimelineViewStyles();
  const isStart = marker.type === 'start';
  
  // Use the reusable smart positioning function for time markers
  const { containerTransform, paperTransform, lineTransform } = calculateTimeMarkerSmartPositioning(marker.position);
  
  return (
      <Box 
        className={classes.timeMarkerContainer}
        style={{ 
          left: `${marker.position}%`,
          transform: containerTransform
        }}
      >
        {/* Vertical line connecting marker to baseline */}
        <Box
          className={`${classes.timeMarkerVerticalLine} ${isStart ? classes.timeMarkerLineAbove : classes.timeMarkerLineBelow}`}
          style={{ transform: lineTransform }}
        />
        
        {/* Time marker paper */}
        <Paper
          elevation={3}
          className={isStart ? classes.timeMarkerPaperAbove : classes.timeMarkerPaperBelow}
          style={{ transform: paperTransform }}
        >
          <Typography 
            className={classes.timeMarkerDate}
          >
            {marker.time.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric'
            })} {marker.time.toLocaleTimeString('en-US', { 
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        </Paper>
      </Box>
  );
});

TimeMarkerPaper.displayName = 'TimeMarkerPaper';

/**
 * Props interface for the PipelineTimeMarkers component
 */
interface PipelineTimeMarkersProps {
  /** Array of pipeline time markers to render */
  pipelineMarkers: PipelineTimeMarker[];
  /** Total minimum width for the timeline container */
  totalMinWidth: number;
}
/**
 * PipelineTimeMarkers Component
 * 
 * Renders pipeline-based time markers with start/end papers positioned
 * along a timeline baseline. Manages the overall timeline structure and
 * coordinates the display of multiple time marker papers with proper
 * spacing and alignment.
 * 
 * @component
 * @param props - The component props
 * @param props.pipelineMarkers - Array of pipeline time markers to display
 * @param props.totalMinWidth - Minimum width for timeline container
 * 
 * @returns A complete timeline with baseline and positioned time markers
 */
export const PipelineTimeMarkers = React.memo(({ pipelineMarkers, totalMinWidth }: PipelineTimeMarkersProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box className={classes.timeMarkersContainer}>
      {/* Timeline base line */}
      <Box 
        className={classes.timelineBaseLine} 
        style={{ minWidth: `${totalMinWidth}px` }} 
      />
      
      {/* Time markers container */}
      <Box 
        className={classes.timeMarkersContent}
        style={{ minWidth: `${totalMinWidth}px` }}
      >
        {pipelineMarkers.map((marker, index) => (
          <TimeMarkerPaper 
            key={`${marker.pipeline.id}-${marker.type}-${index}`}
            marker={marker} 
            index={index} 
          />
        ))}
      </Box>
    </Box>
  );
});

PipelineTimeMarkers.displayName = 'PipelineTimeMarkers';

/**
 * Props interface for the RegularTimeMarker component
 */
interface RegularTimeMarkerProps {
  /** Time marker object with position and timing data */
  marker: TimeMarker;
  /** Index for unique key generation */
  index: number;
  /** Whether this marker should be highlighted as active */
  isActive: boolean;
}
/**
 * RegularTimeMarker Component
 * 
 * Renders a single regular time marker with tooltip functionality.
 * Used as a fallback when pipeline-specific markers are not available.
 * Features active state styling and formatted date/time display.
 * 
 * @component
 * @param props - The component props
 * @param props.marker - Time marker with position and datetime information
 * @param props.index - Unique index for React key prop
 * @param props.isActive - Whether to apply active styling
 * 
 * @returns A styled time marker with tooltip and optional active state
 */
export const RegularTimeMarker = React.memo(({ marker, index, isActive }: RegularTimeMarkerProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Tooltip 
      key={index}
      title={getFullDateTimeLabel(marker.fullDateTime)}
      placement="top"
      arrow
    >
      <Box 
        className={`${classes.timeMarker} ${isActive ? classes.timeMarkerActive : ''}`}
      >
        <Paper
          elevation={2}
          className={`${classes.timeMarkerPaper} ${classes.timeMarkerPaperRegular}`}
        >
          <Box className={classes.timeMarkerContent}>
            <Typography 
              className={classes.timeMarkerDate}
              style={{ fontSize: '0.7rem', fontWeight: 600 }}
            >
              {marker.fullDateTime.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric'
              })} {marker.fullDateTime.toLocaleTimeString('en-US', { 
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Tooltip>
  );
});

RegularTimeMarker.displayName = 'RegularTimeMarker';

/**
 * Props interface for the RegularTimeMarkers component
 */
interface RegularTimeMarkersProps {
  /** Array of time markers to render */
  timeMarkers: TimeMarker[];
}
/**
 * RegularTimeMarkers Component
 * 
 * Renders regular time markers as a fallback when pipeline-specific
 * markers are not available. Creates a timeline baseline and positions
 * time markers with one designated as active (typically the middle marker).
 * Provides consistent timeline visualization regardless of data source.
 * 
 * @component
 * @param props - The component props
 * @param props.timeMarkers - Array of time markers to display
 * 
 * @returns A timeline with baseline and regular time markers
 */
export const RegularTimeMarkers = React.memo(({ timeMarkers }: RegularTimeMarkersProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box className={classes.timeMarkersContainer}>
      {/* Timeline base line */}
      <Box className={classes.timelineBaseLine} />
      
      <Box className={classes.timeMarkersContent}>
        {timeMarkers.map((marker, index) => {
          const isActive = index === Math.floor(timeMarkers.length / 2); // Middle marker as active
          
          return (
            <RegularTimeMarker
              key={index}
              marker={marker}
              index={index}
              isActive={isActive}
            />
          );
        })}
      </Box>
    </Box>
  );
});

RegularTimeMarkers.displayName = 'RegularTimeMarkers';
